/**
 * Вода района по файлу ирригации на экране «Вода» (хоким читает, менеджер видит
 * то же). Перестройка по UX (журнал №1450–1469): сначала то, что требует
 * действия, справка — ниже и свёрнута.
 *
 * - «Где разобраться» — пять хозяйств, где больше ЗЕМЛИ без подачи, и «все →»;
 *   пары без площади (агрегаты дехкан, №1451) — отдельной строкой «без площади»,
 *   не на первом месте.
 * - Справка (свёрнута): пять крупнейших каналов по м³ и «все каналы →» на
 *   «Подробно о воде», где стоит полная таблица «канал → хозяйства»; вне плана,
 *   вне агроцикла и вода без земли.
 *
 * Все числа — слой метрик `water.*` и поля `/water/district`; м³ и га рядом, но
 * не смешиваются в одной доле. «Ждём» — окно идёт, «без подачи» — окно
 * закрылось без события.
 */
import { Link } from 'react-router-dom';
import { api, request, type Schemas, type WaterDistrict } from '../../../api/client';
import { useRequest, type RequestResult } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { InfoPopover } from '../../../components/InfoPopover';
import { Badge } from '../../ui/Badge';
import { Card, Stack } from '../../ui/Card';
import { DataTable, type Column } from '../../ui/DataTable';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError } from '../../ui/LoadError';
import { SkeletonCard } from '../../ui';
import { countOf, haUnit, m3Compact, num } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import { metricById, metricText } from './waterFile';
import './water-file.css';

type NoDelivery = WaterDistrict['no_delivery_by_farm'][number];
type Detail = Schemas['WaterDetailOut'];
type Canal = Detail['canals'][number];

/** Хозяйств в «где разобраться» и каналов в справке до «все →». */
const TOP = 5;

const m = defineMessages({
  colFarm: { id: 'water.districtFile.col.farm', defaultMessage: 'Хозяйство', description: 'Заголовок колонки таблицы «Где разобраться»: хозяйство' },
  farmFallback: { id: 'water.districtFile.farmFallback', defaultMessage: 'хозяйство {id}', description: 'Имя хозяйства в таблице, если сервер имя не назвал; {id} — номер хозяйства' },
  colPairs: { id: 'water.districtFile.col.pairs', defaultMessage: 'Поливов без подачи', description: 'Заголовок числовой колонки: сколько пар «контур × полив» остались без подачи; узкая колонка' },
  unitPairs: { id: 'water.districtFile.unit.pairs', defaultMessage: 'пар', description: 'Единица под заголовком колонки «Поливов без подачи»: пары «контур × полив»; одно слово' },
  colNoDeliveryLand: { id: 'water.districtFile.col.noDeliveryLand', defaultMessage: 'Земля без подачи', description: 'Заголовок числовой колонки таблицы «Где разобраться»: земля (га) пар «контур × полив», окно которых закрылось без события в файле; по ней отсортирована таблица' },
  unitHa: { id: 'water.districtFile.unit.ha', defaultMessage: 'га', description: 'Единица под заголовком колонки «Земля»: гектары земли (уникальной, каждый гектар один раз)' },
  noData: { id: 'water.districtFile.noData', defaultMessage: 'нет данных', description: 'Значение вместо числа, когда сервер его не назвал: не ноль, а отсутствие данных' },
  loadWhat: { id: 'water.districtFile.loadWhat', defaultMessage: 'воду района по файлу ирригации', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …» в блоке «Вода района по файлу ирригации»' },
  loading: { id: 'water.districtFile.loading', defaultMessage: 'Читаем файл ирригации…', description: 'Подпись в скелетоне блока, пока файл ирригации читается' },
  filesLink: { id: 'water.districtFile.filesLink', defaultMessage: 'Файлы и споры →', description: 'Ссылка в шапке блока на экран менеджера: файлы ирригации и споры фермеров' },
  noFileTitle: { id: 'water.districtFile.noFile.title', defaultMessage: 'Файлов ирригации нет', description: 'Заголовок пустого состояния блока: ни одного файла ирригации не загружено' },
  // --- где разобраться
  whereTitle: { id: 'water.districtFile.where.title', defaultMessage: 'Где разобраться', description: 'Заголовок блока на экране «Вода» под поливами: хозяйства, где больше всего земли осталось без подачи воды — с чего начать разбор' },
  whereInfo: {
    id: 'water.districtFile.where.info',
    defaultMessage:
      'Хозяйства выбранного агроцикла, где больше всего земли без подачи: окно полива закрылось, а события в файле ирригации нет. Порядок — по земле (га), а не по числу пар: у мелких участков пар много, а земли мало. Файл может опаздывать, поэтому «не полито» не говорится.',
    description: 'Текст всплывающего пояснения блока «Где разобраться»',
  },
  allFarms: { id: 'water.districtFile.where.allFarms', defaultMessage: 'Все хозяйства →', description: 'Ссылка под таблицей «Где разобраться»: полный список хозяйств без подачи на экране «Подробно о воде»' },
  noDeliveryEmptyTitle: { id: 'water.districtFile.noDelivery.emptyTitle', defaultMessage: 'Поливов без подачи нет', description: 'Заголовок пустого состояния таблицы «Где разобраться»' },
  noDeliveryEmptyReason: { id: 'water.districtFile.noDelivery.emptyReason', defaultMessage: 'у закрывшихся окон выбранного агроцикла есть события файла', description: 'Пояснение пустого состояния: у всех закрывшихся окон полива есть события в файле; строчными, после заголовка' },
  farmsDenominator: {
    id: 'water.districtFile.denominator.farms',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Единица знаменателя под таблицей («показано 6 из 24 хозяйств»); только слово, без числа',
  },
  noLandTitle: { id: 'water.districtFile.noLand.title', defaultMessage: 'Без площади', description: 'Подзаголовок под таблицей «Где разобраться»: хозяйства, у которых есть поливы без подачи, но земля этих поливов не известна (почти ноль га)' },
  noLandInfoLabel: { id: 'water.districtFile.noLand.infoLabel', defaultMessage: 'Почему без площади', description: 'Подпись кнопки-«i» у подзаголовка «Без площади»' },
  noLandInfo: {
    id: 'water.districtFile.noLand.info',
    defaultMessage:
      'Чаще всего это дехканские хозяйства и их сводные записи: много мелких участков (частей контура), у каждого — свой полив, поэтому пар много. Площадь посева этих участков в размещении почти нулевая, и земля без подачи выходит «0 га». Число пар здесь не сравнивается с землёй хозяйств выше; площадь нужно уточнить в размещении.',
    description: 'Текст всплывающего пояснения «Без площади»: почему у дехканских хозяйств много поливов без подачи и ноль га',
  },
  noLandItem: { id: 'water.districtFile.noLand.item', defaultMessage: '<farm>{name}</farm> · {pairs}', description: 'Строка списка «Без площади»: хозяйство (ссылка) и число поливов без подачи; <farm> — ссылка на хозяйство, {name} — имя, {pairs} — «462 пары»' },
  // --- справка
  refCanalsTitle: { id: 'water.districtFile.ref.canalsTitle', defaultMessage: 'Каналы агроцикла: пять крупнейших по объёму', description: 'Подзаголовок в свёрнутой справке экрана «Вода»: пять каналов с наибольшим объёмом воды поливов выбранного агроцикла по файлу ирригации' },
  refCanalsAria: { id: 'water.districtFile.ref.canalsAria', defaultMessage: 'Пять крупнейших каналов по объёму воды, м³', description: 'Подпись диаграммы каналов для программ чтения с экрана' },
  canalName: { id: 'water.districtFile.canals.name', defaultMessage: 'канал {label}', description: 'Название канала в заголовке карточки канала; {label} — имя канала из данных' },
  canalLand: { id: 'water.districtFile.ref.canalLand', defaultMessage: '{volume} · {land}', description: 'Подпись у полосы канала: объём и земля; {volume} — «35,3 млн м³» или «нет данных», {land} — «5 841,6 га» или «нет данных»' },
  allCanals: { id: 'water.districtFile.ref.allCanals', defaultMessage: 'Все {canals} и их хозяйства — «Подробно о воде» →', description: 'Ссылка под диаграммой каналов: полная таблица «канал → хозяйства» на экране «Подробно о воде»; {canals} — число каналов словом в винительном падеже («22 канала»)' },
  noCanalsTitle: { id: 'water.districtFile.canals.emptyTitle', defaultMessage: 'Каналов с поливом нет', description: 'Заголовок пустого состояния списка каналов' },
  noCanalsReason: { id: 'water.districtFile.canals.emptyReason', defaultMessage: 'событий файла ирригации, привязанных к земле, в срезе нет', description: 'Пояснение пустого состояния списка каналов; строчными, после заголовка' },
  canalsCount: { id: 'water.districtFile.ref.canalsCount', defaultMessage: '{count, plural, one {# канал} few {# канала} many {# каналов} other {# канала}}', description: 'Число каналов словом в ссылке справки: «22 канала»' },
  refSummary: {
    id: 'water.districtFile.ref.summary',
    defaultMessage: 'каналы агроцикла · вне плана {unplanned} · вне агроцикла {outside} · без земли {unmatched}',
    description: 'Сводка свёрнутой справки экрана «Вода»; {unplanned} — события вне плана, {outside} — вода вне агроцикла (м³), {unmatched} — вода без земли (м³); значения с единицами или «нет данных»',
  },
  extraLabel: { id: 'water.districtFile.extra.label', defaultMessage: 'Вне плана, вне агроцикла и вода без земли', description: 'Заголовок раскрывающегося блока: три слоя воды, не вошедшие в план полива' },
  extraNote: {
    id: 'water.districtFile.extra.note',
    defaultMessage:
      'Вне плана — агроцикл на земле есть, плана полива нет; вне агроцикла — агроцикла нет; без земли — строки файла без контура. Итог района = привязанные события + вода без земли, показываются раздельно: кубометры не теряются.',
    description: 'Пояснение в раскрытом блоке «Вне плана, вне агроцикла и вода без земли»',
  },
  extraUnplanned: { id: 'water.districtFile.extra.unplanned', defaultMessage: 'Поливы вне плана: {events}, {volume}', description: 'Строка списка: полив вне плана; {events} — число событий, {volume} — объём воды (м³)' },
  extraOutside: { id: 'water.districtFile.extra.outside', defaultMessage: 'Вне агроцикла: {volume}, земля {land}', description: 'Строка списка: вода вне агроцикла; {volume} — объём (м³), {land} — земля (га)' },
  extraUnmatched: { id: 'water.districtFile.extra.unmatched', defaultMessage: 'Вода без земли: {volume}', description: 'Строка списка: вода из строк файла без контура; {volume} — объём (м³)' },
});

const farmLabel = (r: NoDelivery): string => r.label ?? tr(m.farmFallback, { id: r.key });

const whereColumns = (): Column<NoDelivery>[] => [
  { key: 'name', header: tr(m.colFarm), render: (r) => <Link to={`/farms/${r.key}?tab=water`}>{farmLabel(r)}</Link> },
  { key: 'land', header: tr(m.colNoDeliveryLand), unit: tr(m.unitHa), numeric: true, render: (r) => (r.land_ha === null ? tr(m.noData) : haUnit(r.land_ha)) },
  { key: 'pairs', header: tr(m.colPairs), unit: tr(m.unitPairs), numeric: true, render: (r) => (r.pairs === null ? tr(m.noData) : num(r.pairs)) },
];

/**
 * Вода района одним запросом на экран: KPI, «где разобраться» и справка читают
 * один ответ. Пока список агроциклов не пришёл (`ready` ложно), срез не просим:
 * агроцикл сужает метрики плана, и без него запрос ушёл бы дважды.
 */
export function useWaterDistrict(cycle: string | null, ready: boolean): RequestResult<WaterDistrict | null> {
  const { viewParams } = useAnalysisContext();
  return useRequest(
    async () => (ready ? api.waterDistrict({ ...viewParams, ...(cycle ? { cycle } : {}) }) : null),
    [JSON.stringify(viewParams), cycle ?? '', ready],
  );
}

/** Текст метрики района или «нет данных». */
export function districtMetric(d: WaterDistrict | null, id: string): string {
  if (!d) return '…';
  const found = metricById(d.metrics, id);
  return found ? metricText(found) : tr(m.noData);
}

/** «Где разобраться»: топ хозяйств по земле без подачи и отдельно — без площади. */
export function WaterWhereToLook({ res, allTo }: { res: RequestResult<WaterDistrict | null>; allTo: string }) {
  const t = useT();
  if (res.data === null) {
    if (res.state === 'error') return <LoadError compact what={t(m.loadWhat)} error={res.error} onRetry={res.reload} />;
    return (
      <SkeletonCard quiet title={t(m.whereTitle)}>
        <span className="t-caption">{t(m.loading)}</span>
      </SkeletonCard>
    );
  }
  const d = res.data;
  const noFile = d.freshness.versions === 0;
  const noLand = d.no_delivery_without_land;
  return (
    <div data-guide="where">
      <Card
        title={t(m.whereTitle)}
        info={t(m.whereInfo)}
        aside={
          <span className="wf-step">
            <Badge tone={noFile ? 'copper' : 'outline'}>{d.freshness.note}</Badge>
            <Link to="/ops/water">{t(m.filesLink)}</Link>
          </span>
        }
      >
        {noFile ? (
          <EmptyState compact title={t(m.noFileTitle)} reason={d.freshness.note} />
        ) : (
          <Stack tight>
            <DataTable
              columns={whereColumns()}
              rows={d.no_delivery_by_farm}
              getKey={(r) => r.key}
              denominator={m.farmsDenominator}
              limit={TOP}
              more={{ to: allTo, label: t(m.allFarms) }}
              empty={{ title: t(m.noDeliveryEmptyTitle), reason: t(m.noDeliveryEmptyReason) }}
            />
            {noLand.length > 0 ? (
              <div className="wf-noland">
                <span className="wf-noland__title">
                  {t(m.noLandTitle)}
                  <InfoPopover icon="info" label={t(m.noLandInfoLabel)}>
                    {t(m.noLandInfo)}
                  </InfoPopover>
                </span>
                <ul className="wf-noland__list">
                  {noLand.map((r) => (
                    <li key={r.key}>
                      {t(m.noLandItem, {
                        farm: (c) => <Link to={`/farms/${r.key}?tab=water`}>{c}</Link>,
                        name: farmLabel(r),
                        // i18n-ignore: код единицы из ответа сервера (слой метрик), не текст интерфейса
                        pairs: r.pairs === null ? t(m.noData) : metricText({ value: r.pairs, unit: 'пар' }),
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Stack>
        )}
      </Card>
    </div>
  );
}

/** Сводка свёрнутой справки: три слоя воды вне плана. */
export function districtReferenceSummary(d: WaterDistrict): string {
  return tr(m.refSummary, {
    unplanned: districtMetric(d, 'water.unplanned_events'),
    outside: districtMetric(d, 'water.outside_cycle_m3'),
    unmatched: districtMetric(d, 'water.unmatched_m3'),
  });
}

const numOrNull = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Пять крупнейших каналов агроцикла по м³ — полосы одной шкалы; полная таблица —
 * «Подробно о воде». Читает тот же ответ `/water/detail`, что и та страница, и
 * только когда справку раскрыли: числа двух экранов сходятся (журнал №1452).
 */
function CanalTop({ cycle, allTo }: { cycle: string; allTo: string }) {
  const t = useT();
  const { viewParams } = useAnalysisContext();
  const res = useRequest(() => request<Detail>('/water/detail', { ...viewParams, cycle }), [JSON.stringify(viewParams), cycle]);
  if (res.data === null) {
    if (res.state === 'error') return <LoadError compact what={t(m.refCanalsAria)} error={res.error} onRetry={res.reload} />;
    return <span className="t-caption">{t(m.loading)}</span>;
  }
  const canals: Canal[] = res.data.canals;
  if (canals.length === 0) return <EmptyState compact title={t(m.noCanalsTitle)} reason={t(m.noCanalsReason)} />;
  const top = canals.slice(0, TOP);
  const max = Math.max(1, ...top.map((c) => numOrNull(c.volume_m3) ?? 0));
  return (
    <Stack tight>
      <ul className="wf-bars" aria-label={t(m.refCanalsAria)}>
        {top.map((c) => {
          const volume = numOrNull(c.volume_m3);
          const land = numOrNull(c.area_ha);
          return (
            <li key={c.label} className="wf-bar">
              <span className="wf-bar__name" title={c.label}>{t(m.canalName, { label: c.label })}</span>
              <span className="wv-chan__track" aria-hidden="true">
                <span className="wv-chan__fill" style={{ width: `${Math.min(100, ((volume ?? 0) / max) * 100)}%` }} />
              </span>
              <span className="wf-bar__val">
                {t(m.canalLand, {
                  volume: volume === null ? t(m.noData) : m3Compact(volume),
                  land: land === null ? t(m.noData) : haUnit(land),
                })}
              </span>
            </li>
          );
        })}
      </ul>
      <Link className="linkish" to={allTo}>{t(m.allCanals, { canals: countOf(canals.length, m.canalsCount) })}</Link>
    </Stack>
  );
}

/**
 * Справка района по файлу ирригации — содержимое свёрнутого блока экрана
 * «Вода»: каналы и вода вне плана. Раскрывается по нажатию.
 */
export function WaterDistrictReference({ d, cycle, allCanalsTo }: { d: WaterDistrict; cycle: string; allCanalsTo: string }) {
  const t = useT();
  if (d.freshness.versions === 0) return <EmptyState compact title={t(m.noFileTitle)} reason={d.freshness.note} />;
  const text = (id: string) => districtMetric(d, id);
  return (
    <Stack>
      <div className="wv-section">
        <h4 className="wv-h">{t(m.refCanalsTitle)}</h4>
        <CanalTop cycle={cycle} allTo={allCanalsTo} />
      </div>
      <div className="wv-section">
        <h4 className="wv-h">{t(m.extraLabel)}</h4>
        <p className="t-caption">{t(m.extraNote)}</p>
        <ul className="wv-plain">
          <li>{t(m.extraUnplanned, { events: text('water.unplanned_events'), volume: text('water.unplanned_m3') })}</li>
          <li>{t(m.extraOutside, { volume: text('water.outside_cycle_m3'), land: text('water.outside_cycle_land_ha') })}</li>
          <li>{t(m.extraUnmatched, { volume: text('water.unmatched_m3') })}</li>
        </ul>
      </div>
      {d.notes.map((n) => (
        <p key={n} className="t-caption">
          {n}
        </p>
      ))}
    </Stack>
  );
}
